// useVersionCheck — polls /version.json to detect new deployments
// When a version mismatch is found, reloads the page to pick up the new service worker

import { useEffect, useRef } from 'react';

const VERSION_URL = '/version.json';
const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

type VersionPayload = Readonly<{
  readonly version: string;
}>;

const fetchVersion = async (): Promise<string | null> => {
  try {
    const res = await fetch(VERSION_URL, { cache: 'no-cache' });
    if (!res.ok) return null;
    const data = (await res.json()) as VersionPayload;
    return data.version ?? null;
  } catch {
    return null;
  }
};

export const useVersionCheck = (): void => {
  const knownVersionRef = useRef<string | null>(null);

  useEffect(() => {
    const check = async (): Promise<void> => {
      const latest = await fetchVersion();
      if (latest === null) return;

      if (knownVersionRef.current === null) {
        // First check — record the version we booted with
        knownVersionRef.current = latest;
        return;
      }

      if (latest !== knownVersionRef.current) {
        // New version deployed — reload to activate new SW
        window.location.reload();
      }
    };

    // Initial check after short delay (don't block first paint)
    const initialTimer = setTimeout(check, 3000);
    const interval = setInterval(check, CHECK_INTERVAL_MS);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, []);
};
